import { addMinutes } from "date-fns";
import { resolveDefaultAprovalStatus } from "../../../../components/Collaboration/StatusChange/status-change.utils";
import { POST_STATES } from "../../../../constants/PostsView";
import {
  FormValidationErrors,
  FormValidationWarnings,
  PostFormState,
} from "../../../../shared/types/post";
import { CollaborationPostStatus, User } from "../../../user/types";
import { Card } from "./post-mini-card";
import { getScheduleDisplay } from "../../../../components/Posts/PostForm/PostFormActions/post-form-actions.utils";
import sumBy from "lodash/sumBy";
import { filterAccountsByPlatformType } from "../../../../utils/accounts";
import { getPostedState, resolveImgThumbnail } from "../../../../utils/posts";
import { hasApprovals } from "../../../../utils/feature-gates";

export const formToMiniCardState = ({
  form,
  isSelected,
  user,
  userCollabSettings,
}: {
  form: PostFormState;
  isSelected?: boolean;
  user: User;
  userCollabSettings: CollaborationPostStatus[];
}): Card => {
  const issues = gatherErrorWarnings(form);
  return {
    media: resolveMedia(form),
    editState: form.isDirty && !form.formSaveData ? "editing" : "saved",
    ...issues,
    postType: resolvePostType(
      form,
      user.preferences?.timePreference,
      user.timezone
    ),
    postStatus: resolvePostStatus({
      form,
      user,
      userCollabSettings,
    }),
    isSelected,
    postId: form.post._id,
    clusterId: form.post.postClusterId,
    form,
  } satisfies Card;
};

function resolvePostType(
  form: PostFormState,
  timePreference: string,
  timezone?: string
): Card["postType"] {
  const { post, postState } = form;
  switch (postState) {
    case POST_STATES.DRAFT: {
      return { type: "draft" };
    }
    case POST_STATES.ARCHIVED: {
      return { type: "archived" };
    }
    case POST_STATES.QUEUED: {
      return { type: "queued" };
    }
    case POST_STATES.SCHEDULED: {
      const when = post.when || addMinutes(new Date(), 5);
      return {
        type: "scheduled",
        scheduledTime: getScheduleDisplay(when, timePreference, timezone),
        isNow: !!post.isNow,
      };
    }
    default: {
      return { type: "draft" };
    }
  }
}

function resolvePostStatus({
  form,
  user,
  userCollabSettings,
}: {
  form: PostFormState;
  user: User;
  userCollabSettings: CollaborationPostStatus[];
}) {
  if (!hasApprovals(user)) {
    return {
      text: "Ready to publish",
      color: "var(--color-green-500)",
    };
  }

  function resolveKey() {
    const { defaultSetting } = resolveDefaultAprovalStatus(
      user,
      userCollabSettings
    );
    return form.post.postStatusKey || defaultSetting?.key || "readytopublish";
  }
  const key = resolveKey();
  const setting = userCollabSettings.find((s) => s.key === key);
  if (!setting) {
    return {
      color: "var(--color-neutral-300)",
      text: "Work in progress",
    };
  }
  return {
    color: setting.color,
    text: setting.label,
  };
}

function countWarnings(warnings: FormValidationWarnings) {
  return sumBy(Object.values(warnings || {}), (w) => {
    if (Array.isArray(w.warnings)) {
      return w.warnings.length;
    }
    return Object.values(w.warnings).length;
  });
}
function countErrors(errors: FormValidationErrors) {
  return sumBy(Object.values(errors || {}), (w) => {
    if (Array.isArray(w.errors)) {
      return w.errors.length;
    }
    return Object.values(w.errors).length;
  });
}

function gatherErrorWarnings(
  form: PostFormState
): Pick<Card, "warningCount" | "errorCount"> {
  if (getPostedState(form.post) === "success") {
    return { warningCount: 0, errorCount: 0 };
  }

  return {
    warningCount: countWarnings(form.validation?.warnings || {}),
    errorCount: countErrors(form.validation?.errors || {}),
  };
}

function resolveMedia(form: PostFormState): Card["media"] {
  const { post } = form;
  const type =
    post.storyType && post.isStory ? post.storyType || "story" : post.type;

  const hasInstagram = form.post.accounts?.some(
    filterAccountsByPlatformType("IG")
  );
  const hasFacebook = form.post.accounts?.some(
    filterAccountsByPlatformType("FB")
  );
  const hasIgFb = hasInstagram || hasFacebook;
  function resolveVideoThumbnail() {
    const allMedia = [...(post.media || []), ...(post.mediaFiles || [])];
    const videoMedia = allMedia.find((c) => c.isVideo || c.type === "video");
    const result =
      form.coverPreviewUrl ||
      videoMedia?.thumbnailUrl ||
      post.thumbnailUrl ||
      "";

    return result;
  }
  const imgThumbnail = resolveImgThumbnail(post);
  switch (type) {
    case "text": {
      return {
        type: "text",
        text: post.caption || "",
      };
    }
    case "image":
    case "document":
    case "igtv": {
      return {
        type,
        thumbnail: imgThumbnail,
      };
    }
    case "reel":
    case "fb-reel": {
      return {
        type: "reel",
        thumbnail: resolveVideoThumbnail(),
      };
    }

    case "video": {
      if ((post.videoPostType === "reel" && hasIgFb) || hasInstagram) {
        return {
          type: "reel",
          thumbnail: resolveVideoThumbnail(),
        };
      }
      return {
        type,
        thumbnail: resolveVideoThumbnail(),
      };
    }
    case "multiMedia": {
      return {
        type: "carousel",
        thumbnail: imgThumbnail,
      };
    }
    case "story": {
      return {
        type: "story",
        thumbnail: resolveVideoThumbnail(),
      };
    }

    default: {
      return {
        type: "text",
        text: post.caption || "",
      };
    }
  }
}
